import { type CSSProperties } from 'react';

import { getWorkspaceAgentPanelWidthCSSValue } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentPanelWidthCSSValue';
import { WORKSPACE_AGENT_PANEL_ASSET_VIEW_HEADER_TOP } from '~/components/v2/components/workspace/workspace-agent-panel/workspaceAgentPanelConstants';
import { WORKSPACE_PANEL_EDGE_PADDING, WORKSPACE_UI2026_SURFACE_GAP } from '~/constants/panelWidths';

export type WorkspaceAgentPanelDockedSpacerRect = {
  height: number;
  /**
   * Distance from the viewport's right edge to the spacer's right edge (`window.innerWidth - rect.right`).
   * Anchors the portalled clip so CSS `width-in` grows left, matching an in-flow last flex child.
   */
  insetRight: number;
  left: number;
  top: number;
  width: number;
};

export type WorkspaceAgentPanelSlotWidthAnimationStyle = CSSProperties & {
  '--tw-animation-width': string;
};

export type WorkspaceAgentPanelDockedLayout = {
  panelStyle: CSSProperties;
  /**
   * ui2026 only: viewport box anchored to the spacer's right edge at the *stored* width. CSS
   * `animate-width-in` / `animate-width-out` change the clip's width on the compositor; the inner
   * card stays at stored width (AdDetailsSidebar / sidenav / custom-fields pattern).
   */
  slotClipStyle: CSSProperties | null;
};

type GetWorkspaceAgentPanelDockedLayoutParams = {
  spacerRect: WorkspaceAgentPanelDockedSpacerRect;
  ui2026: boolean;
};

type GetWorkspaceAgentPanelSlotWidthAnimationStyleParams = {
  useCssWidthVariable: boolean;
  width: number;
};

type GetWorkspaceAgentPanelUnmeasuredDockedStyleParams = {
  ui2026: boolean;
  width: number;
};

/**
 * `--tw-animation-width` is what `animate-width-in` / `animate-width-out` interpolate to. Same
 * pairing as `animation-width-[350px]` + `w-[350px]` on `AdDetailsSidebarContainer`.
 */
export const getWorkspaceAgentPanelSlotWidthAnimationStyle = ({
  useCssWidthVariable,
  width,
}: GetWorkspaceAgentPanelSlotWidthAnimationStyleParams): WorkspaceAgentPanelSlotWidthAnimationStyle => {
  if (useCssWidthVariable) {
    const cssWidth = getWorkspaceAgentPanelWidthCSSValue();

    return {
      '--tw-animation-width': cssWidth,
      maxWidth: cssWidth,
      width: cssWidth,
    };
  }

  return {
    '--tw-animation-width': `${width}px`,
    maxWidth: width,
    width,
  };
};

/**
 * Keep the portalled card mounted at stored width while the spacer's first measure is in flight.
 * `visibility: hidden` rather than `null` so open / rehydrate / dock-from-float do not unmount the
 * instance for a layout frame and leave the gutter empty.
 */
export const getWorkspaceAgentPanelUnmeasuredDockedStyle = ({
  ui2026,
  width,
}: GetWorkspaceAgentPanelUnmeasuredDockedStyleParams): CSSProperties => {
  if (ui2026) {
    const cssWidth = getWorkspaceAgentPanelWidthCSSValue();

    return {
      height: '100%',
      maxWidth: cssWidth,
      visibility: 'hidden',
      width: cssWidth,
    };
  }

  return {
    maxWidth: width,
    visibility: 'hidden',
    width,
  };
};

/**
 * Pair with `getWorkspaceAgentPanelUnmeasuredDockedStyle` on ui2026 so `PortalledWorkspaceAgentPanel`
 * keeps its clip wrapper in the tree. A `null` clip until measure would swap unclipped `Root` for
 * `Presence > clip > Root` and remount composer / TipTap.
 */
export const getWorkspaceAgentPanelUnmeasuredDockedClipStyle = (): CSSProperties => ({
  visibility: 'hidden',
});

/**
 * Right-anchored overflow clip under the asset modal header. Same CSS width-in as the workspace
 * spacer clip; `top` / `bottom` / `right` stay put so closing the modal cannot remount a 0→width
 * enter on an already-open panel.
 */
export const getWorkspaceAgentPanelAssetViewClipStyle = (ui2026: boolean): CSSProperties => ({
  bottom: ui2026 ? WORKSPACE_UI2026_SURFACE_GAP : WORKSPACE_PANEL_EDGE_PADDING,
  right: ui2026 ? WORKSPACE_UI2026_SURFACE_GAP : WORKSPACE_PANEL_EDGE_PADDING,
  top: WORKSPACE_AGENT_PANEL_ASSET_VIEW_HEADER_TOP,
});

const getWorkspaceAgentPanelUi2026DockedLayout = (
  spacerRect: WorkspaceAgentPanelDockedSpacerRect,
): WorkspaceAgentPanelDockedLayout => {
  const cssWidth = getWorkspaceAgentPanelWidthCSSValue();

  return {
    panelStyle: {
      height: '100%',
      maxWidth: cssWidth,
      width: cssWidth,
    },
    slotClipStyle: {
      height: spacerRect.height,
      right: spacerRect.insetRight,
      top: spacerRect.top,
    },
  };
};

/**
 * Pre-ui2026 mirrors the spacer box (the card width is the slot). ui2026 keeps the stored width
 * inside a right-anchored overflow clip so the rounded card is not squashed, and so ResizeObserver
 * does not rewrite clip width every animation frame.
 */
export const getWorkspaceAgentPanelDockedLayout = ({
  spacerRect,
  ui2026,
}: GetWorkspaceAgentPanelDockedLayoutParams): WorkspaceAgentPanelDockedLayout => {
  if (!ui2026) {
    return {
      panelStyle: {
        height: spacerRect.height,
        left: spacerRect.left,
        maxWidth: spacerRect.width,
        top: spacerRect.top,
        width: spacerRect.width,
      },
      slotClipStyle: null,
    };
  }

  return getWorkspaceAgentPanelUi2026DockedLayout(spacerRect);
};
